import { useEffect, useState, type ReactNode } from 'react';
import { cn } from '@/lib/cn';

/**
 * A set of illustrations that cross-fades from one to the next on a timer.
 *
 * This replaces the previous scheme, where one picture was drawn at random per mount and
 * held until you navigated away. That only ever showed a returning visitor a second
 * composition, and never showed a first-time visitor more than one. The rotation now runs
 * on its own, so the range of the artwork is visible without the visitor doing anything.
 *
 * Three things are deliberate here:
 *
 * **The order is shuffled per visit, not just the starting picture.** An earlier version
 * picked a random offset into a fixed list, so every visitor saw the same cycle, merely
 * entered at a different point. A visitor who watched two swaps could predict the third,
 * and pairs of illustrations that sit badly together sat badly together for everyone. A
 * fresh permutation per mount means two visitors landing at the same moment do not see the
 * same frame, and a reload does not replay the same sequence.
 *
 * The permutation is drawn once and then cycles unchanged, so the rotation is still a loop
 * with a fixed period — it is which pictures the loop visits in which order that varies,
 * not the pacing.
 *
 * **The siblings are not mounted until the window has loaded.** The first illustration in
 * a rotation is the page's largest contentful paint; mounting the whole set up front would
 * put competing requests in front of it and undo the `fetchPriority` the caller sets. They
 * mount after `load`, which is several seconds before the first swap.
 *
 * **The timer starts with them.** Rotating to a picture that has not been mounted yet
 * would fade to an empty frame, so the interval is keyed on the same flag rather than
 * started on mount.
 *
 * Under `prefers-reduced-motion: reduce` nothing rotates: the first picture of the shuffled
 * order stays put for the visit. An image that changes itself every few seconds is exactly
 * the kind of unsolicited motion that setting is asking us to stop.
 */
export interface RotatingArtworkProps {
  /**
   * Root-relative paths *without* an extension, e.g. `/heroimg1`.
   *
   * Each needs a `.webp` and a `.png` sibling in `public`. Every picture in one set must
   * share a canvas — see `width`/`height`.
   */
  names: readonly string[];
  /**
   * The canvas every picture in the set is drawn on, used as an `aspect-ratio`.
   *
   * The frame is reserved from the first paint, so nothing shifts when the images arrive
   * and nothing moves when one fades into the next. This is only sound because the images
   * are normalised to a single canvas before they land in `public`; a set with mixed
   * aspect ratios would letterbox against it.
   */
  width: number;
  height: number;
  /**
   * Marks the first picture as the page's LCP element: eager, and `fetchPriority="high"`.
   *
   * At most one element on a page should claim this. The hero does; the contact banner
   * wants `eager` without the priority — see below.
   */
  priority?: boolean;
  /**
   * Fetch the first picture immediately, at normal priority.
   *
   * For artwork that is above the fold but is not the LCP element, which is the contact
   * banner on `/contact`: it sits under that page's heading, and marking it high priority
   * would put it in front of the thing that actually is the largest paint.
   */
  eager?: boolean;
  /** Sits on the wrapper, which owns the layout box. */
  className?: string;
  /** Sits on every `<img>`. */
  imageClassName?: string;
}

/**
 * How long each picture is held, start of one fade to the start of the next.
 *
 * One constant for the hero, the contact banner and the About illustration: they are three
 * instances of one component, so the site changes pace in one place rather than drifting
 * into three different rhythms.
 *
 * Net of the one-second fade this leaves each composition six seconds still — long enough
 * to read, short enough that a visitor sees a second one before scrolling past.
 */
const ROTATION_MS = 7000;

/**
 * The cross-fade itself.
 *
 * A second of dissolve against seven of hold: long enough to read as a fade rather than a
 * cut, short enough that the two compositions are not superimposed for any length of time.
 * Both illustrations are painted at once through the whole of it, and these are dense
 * pictures — a longer overlap reads as clutter rather than as a transition.
 */
const FADE_MS = 1000;

/**
 * Read in an effect rather than in a state initializer, the same way `useReveal` and
 * `useCountUp` do it.
 */
function prefersReducedMotion(): boolean {
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

/**
 * A Fisher-Yates shuffle of `0..length - 1`, giving the order the rotation walks.
 *
 * Indices rather than the names themselves: `names` is `readonly`, callers pass it as an
 * `as const` literal, and the render still needs each picture's position in the original
 * list to key it and to decide what mounts.
 */
function shuffledOrder(length: number): number[] {
  const order = Array.from({ length }, (_, index) => index);
  for (let i = length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    // A temporary rather than a destructured swap: `noUncheckedIndexedAccess` types both
    // reads as possibly-undefined, and the loop bounds are what rule that out.
    const held = order[i]!;
    order[i] = order[j]!;
    order[j] = held;
  }
  return order;
}

export function RotatingArtwork({
  names,
  width,
  height,
  priority = false,
  eager = false,
  className,
  imageClassName,
}: RotatingArtworkProps): ReactNode {
  // Drawn once per mount and then cycled: advancing a counter and taking it modulo the
  // length keeps the loop infinite without ever indexing past the end.
  const [order] = useState(() => shuffledOrder(names.length));
  const [step, setStep] = useState(0);
  const [mountRest, setMountRest] = useState(false);

  const active = order[step % order.length];
  // The picture the visit opens on, which is the one that carries `priority`/`eager` and
  // the only one mounted before `load`. It has to be read off the shuffle rather than
  // being index 0, or the hero would preload one illustration and then paint a different
  // one — or, before `mountRest`, paint an empty frame.
  const lead = order[0];

  /*
    Hold the siblings back until the page has finished loading — see the note above.

    Reduced motion is checked *here*, not only on the timer below. Mounting the set and
    then declining to rotate it would download every illustration but one for nothing: the
    visitor pays for the whole rotation and sees a single frame of it. That is worth real
    bandwidth on the hero, which is a set of ten.
  */
  useEffect(() => {
    if (names.length < 2 || prefersReducedMotion()) return;
    if (document.readyState === 'complete') {
      setMountRest(true);
      return;
    }
    const onLoad = (): void => setMountRest(true);
    window.addEventListener('load', onLoad, { once: true });
    return () => window.removeEventListener('load', onLoad);
  }, [names.length]);

  useEffect(() => {
    // `mountRest` is already false under reduced motion, so this is belt and braces — but
    // it is the guard that would matter if the set were ever mounted eagerly.
    if (!mountRest || names.length < 2 || prefersReducedMotion()) return;

    const id = window.setInterval(() => setStep((value) => value + 1), ROTATION_MS);
    return () => window.clearInterval(id);
  }, [mountRest, names.length]);

  return (
    /*
      No `z-index`, `transform`, `filter` or `isolation` on this wrapper, and none on the
      images. The hero's ground is dissolved by a `mix-blend-multiply` on an ancestor, and
      every one of those properties creates a stacking context that silently makes the
      blend inert — twice now in this codebase.
    */
    <div
      className={cn('relative w-full', className)}
      style={{ aspectRatio: `${width} / ${height}` }}
    >
      {names.map((name, index) => {
        const isFirst = index === lead;
        // Every picture is in the DOM once mounted; only opacity moves. Cross-fading
        // means both are painted at once mid-transition, which is why the artwork is
        // alpha-cut or drawn on a flat ground rather than blended per image.
        if (!isFirst && !mountRest) return null;

        return (
          <picture key={name}>
            <source srcSet={`${name}.webp`} type="image/webp" />
            <img
              src={`${name}.png`}
              width={width}
              height={height}
              // Decorative. The heading and copy beside it already say what this shows,
              // and announcing a picture that changes on a timer would be worse than
              // silence for a screen reader.
              alt=""
              loading={isFirst && (priority || eager) ? 'eager' : 'lazy'}
              // Only the lead picture competes for bandwidth; the siblings explicitly
              // stand back. `auto` rather than `low` for an eager-but-not-LCP lead, which
              // is the priority the markup this replaced had.
              fetchPriority={isFirst ? (priority ? 'high' : 'auto') : 'low'}
              decoding="async"
              className={cn(
                'absolute inset-0 size-full object-contain',
                // `ease-linear`, not the usual ease-in-out: on a cross-fade the two
                // opposing eased ramps dip through a visibly lighter middle, which is the
                // flicker that made this read as a cut. Linear ramps sum to a constant.
                'transition-opacity ease-linear motion-reduce:transition-none',
                index === active ? 'opacity-100' : 'opacity-0',
                imageClassName,
              )}
              style={{ transitionDuration: `${FADE_MS}ms` }}
            />
          </picture>
        );
      })}
    </div>
  );
}
